import { StopCard } from "../cards/StopCard";
import { LineCard } from "../cards/LineCard";
import type { Stop, LineDetails } from "../../../../types";
import * as React from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { recentItemsAtom, type RecentItem } from "../../../../atoms/recent";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  CompactButton,
  Heading,
  Icon,
  TrafficBadge,
} from "@ogt/ui";
import { isPureNartrafikFromStop } from "../../../../utils/stopType";
import { getLinePublicLabel } from "../../../../utils/lineDisplay";
import { useTrafficMeta } from "../../../../utils/useTrafficStyle";
import { getLineMapColor } from "../../../../utils/lineIdentity";

interface CombinedSectionProps {
  // stops
  selectedStops: Stop[];
  activeStopId: string | null;
  activeStopLocation?: Stop | null;
  onStopRemove: (stopId: string) => void;
  setActiveStopId: (stopId: string) => void;
  onStopSelect: (stop: Stop) => void;
  onAddLineById: (lineId: string) => void;
  onClearStops: () => void;
  visibleNartrafikAreaCount: number;
  onShowNartrafikAreas: () => void;
  onClearActive: () => void;
  activeStopLocations?: Stop[];
  showStopLocationsButton?: boolean;
  onShowStopLocations?: () => void;
  onHideStopLocations?: () => void;

  // lines
  selectedLines: LineDetails[];
  activeLineId: string | null;
  onAddAndActivateLine: (line: LineDetails | string) => void;
  onLineRemove: (lineId: string) => void;
  onRemoveStopGlobal: (stopId: string) => void;
  showLiveByLine: Set<string>;
  onToggleShowLiveForLine: (lineId: string, checked: boolean) => void;
  liveUnavailable?: boolean;
  unavailableLineIds?: Set<string>;
  liveStateByLine?: Record<string, { emptyHits: number; unavailable: boolean }>;

  onAddStopFromLine: (s: Stop) => void;
  allStops?: Stop[];
}

const stopKeyOf = (id: string | number) => String(id).split("-")[0];

const SV_COLLATOR = new Intl.Collator("sv", {
  sensitivity: "base",
  numeric: true,
});

function lineMatchesActive(line: LineDetails, activeLineId: string | null) {
  if (!activeLineId) return false;

  const active = String(activeLineId);

  const candidates = [
    line.id,
    (line as any).LineNrReal,
    (line as any).lineNrReal,
    (line as any).LineName,
    (line as any).lineName,
    (line as any).PublicLineNumber,
    (line as any).publicLineNumber,
  ]
    .filter((x) => x != null && String(x).trim() !== "")
    .map(String);

  return candidates.includes(active);
}

function getLineId(line: LineDetails) {
  return String(
    line.id ??
      (line as any).LineNrReal ??
      (line as any).lineNrReal ??
      (line as any).LineName ??
      (line as any).PublicLineNumber ??
      ""
  );
}

function getStopDisplayName(stop: Stop) {
  return String(stop.name ?? "")
    .replace(/\s*\([^)]*\)\s*$/, "")
    .trim();
}

function getLineDisplayName(line: LineDetails) {
  const publicLabel = getLinePublicLabel(line);
  const municipality = (line as any).municipalityName;

  return municipality
    ? `Linje ${publicLabel}, ${municipality}`
    : `Linje ${publicLabel}`;
}

function StopListButton({
  stop,
  active,
  onActivate,
  onRemove,
}: {
  stop: Stop;
  active?: boolean;
  onActivate: (stop: Stop) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <li
      className={`group relative z-1 flex gap-6 rounded-[var(--box-border-radius)]
      border-0 bg-[var(--card-color-bg-alt)] !p-4 pt-6 pb-8
      ${
        active
          ? "!bg-[var(--card-color-bg-interacted)]"
          : "!border !border-[var(--card-color-border)] has-[.activate-button:hover]:bg-[var(--card-color-bg-interacted,#DED7D2)]"
      }
    `}
    >
      <button
        type="button"
        className="activate-button peer absolute inset-y-0 left-0 w-[calc(100%-116px)] rounded-[var(--box-border-radius)] text-left"
        onClick={() => onActivate(stop)}
        aria-current={active ? "true" : undefined}
        aria-label={`Aktivera ${getStopDisplayName(stop)}`}
      />

      <div className="pointer-events-none relative z-10 flex items-center gap-2 peer-hover:[&>span.displayname]:underline">
        <Badge variant={isPureNartrafikFromStop(stop) ? "blue" : "red"}>
          <Icon size="10" name={isPureNartrafikFromStop(stop) ? "n" : "h"} />
        </Badge>

        <span className="displayname text-sm max-w-[160px]">
          {getStopDisplayName(stop)}
        </span>
      </div>

      <CompactButton
        className="relative z-20 ml-auto h-fit min-w-[86px]"
        variant="secondary"
        iconName="trash"
        size="small"
        showIcon
        onClick={() => onRemove(stop.id)}
      >
        Ta bort
      </CompactButton>
    </li>
  );
}

function LineListButton({
  line,
  active,
  onActivate,
  onRemove,
}: {
  line: LineDetails;
  active?: boolean;
  onActivate: (line: LineDetails) => void;
  onRemove: (id: string) => void;
}) {
  const { meta } = useTrafficMeta(line);
  const lineId = getLineId(line);
  const lineColor = getLineMapColor(line);

  return (
    <li
      className={`relative z-1 flex gap-6 rounded-[var(--box-border-radius)]
    border-0 !border-l-[3px] !border-l-solid
    bg-[var(--card-color-bg-alt)] !p-4 pt-6 pb-8
    ${
      active
        ? "!bg-[var(--card-color-bg-interacted)]"
        : "!border-t !border-r !border-b !border-t-[var(--card-color-border)] !border-r-[var(--card-color-border)] !border-b-[var(--card-color-border)] has-[.activate-button:hover]:bg-[var(--card-color-bg-interacted,#DED7D2)]"
    }
  `}
      style={{ borderLeftColor: lineColor }}
    >
      <button
        type="button"
        className="activate-button peer absolute inset-y-0 left-0 w-[calc(100%-115px)] rounded-[var(--box-border-radius)] text-left"
        onClick={() => onActivate(line)}
        aria-current={active ? "true" : undefined}
        aria-label={`Aktivera ${getLineDisplayName(line)}`}
      />

      <div className="pointer-events-none relative z-10 flex items-center gap-2 peer-hover:[&>span.displayname]:underline">
        <div className="flex shrink-0 flex-col items-center gap-1">
          <TrafficBadge
            className="!min-w-[60px] pr-2"
            meta={meta}
            styleOpts={{ includeCssIconAsFirst: false }}
          >
            {getLinePublicLabel(line)}
          </TrafficBadge>
        </div>

        <span className="displayname text-sm max-w-[160px]">
          {getLineDisplayName(line)}
        </span>
      </div>

      <CompactButton
        className="relative z-20 ml-auto h-fit min-w-[85px]"
        variant="secondary"
        size="small"
        iconName="trash"
        showIcon
        onClick={() => onRemove(lineId)}
      >
        Ta bort
      </CompactButton>
    </li>
  );
}

export function LineAndStopSection({
  // stops
  selectedStops,
  activeStopId,
  activeStopLocation,
  activeStopLocations = [],
  onStopRemove,
  onStopSelect,
  setActiveStopId,
  onAddLineById,
  onClearStops,
  visibleNartrafikAreaCount,
  onShowNartrafikAreas,
  onClearActive,
  showStopLocationsButton = false,
  onShowStopLocations,
  onHideStopLocations,

  // lines
  selectedLines,
  activeLineId,
  onAddAndActivateLine,
  onLineRemove,
  showLiveByLine,
  onToggleShowLiveForLine,
  liveStateByLine,

  // actions
  onAddStopFromLine,

  // data
  allStops,
}: CombinedSectionProps) {
  //const recents = useAtomValue(recentItemsAtom);
  const setRecents = useSetAtom(recentItemsAtom);

  const markSeen = React.useCallback(
    (kind: "stop" | "line", id: string) => {
      setRecents((prev) => {
        const without = prev.filter((x) => !(x.kind === kind && x.id === id));
        return [{ kind, id, ts: Date.now() }, ...without].slice(0, 300);
      });
    },
    [setRecents]
  );

  const removeFromRecents = React.useCallback(
    (kind: RecentItem["kind"], id: string) => {
      setRecents((prev) =>
        prev.filter((x) => !(x.kind === kind && x.id === id))
      );
    },
    [setRecents]
  );

  function groupSelectedStops(stops: Stop[]) {
    const groups = new Map<string, Stop>();

    for (const stop of stops) {
      const rootId = stopKeyOf(stop.id);
      const current = groups.get(rootId);

      if (!current || String(stop.id) === rootId) {
        groups.set(rootId, stop);
      }
    }

    return Array.from(groups.values());
  }

  const selectedStopGroups = React.useMemo(
    () =>
      groupSelectedStops(selectedStops).sort((a, b) =>
        SV_COLLATOR.compare(getStopDisplayName(a), getStopDisplayName(b))
      ),
    [selectedStops]
  );

  const sortedSelectedLines = React.useMemo(
    () =>
      [...selectedLines].sort((a, b) =>
        SV_COLLATOR.compare(getLineDisplayName(a), getLineDisplayName(b))
      ),
    [selectedLines]
  );

  const activeStopKey = activeStopId ? stopKeyOf(activeStopId) : null;

  const activeStop = React.useMemo(() => {
    if (!activeStopKey) return null;

    return (
      selectedStopGroups.find((stop) => stopKeyOf(stop.id) === activeStopKey) ??
      null
    );
  }, [selectedStopGroups, activeStopKey]);

  const activeLine = React.useMemo(() => {
    if (!activeLineId) return null;

    return (
      selectedLines.find((line) => lineMatchesActive(line, activeLineId)) ??
      null
    );
  }, [selectedLines, activeLineId]);

  const activeKind: "line" | "stop" | null = activeStop
    ? "stop"
    : activeLine
    ? "line"
    : null;

  const handleStopRemove = React.useCallback(
    (id: string) => {
      removeFromRecents("stop", stopKeyOf(id));
      onStopRemove(id);
    },
    [removeFromRecents, onStopRemove]
  );

  const handleRemoveStopGlobalByParentId = React.useCallback(
    (id: string) => {
      const rootId = stopKeyOf(id);

      const representative = selectedStopGroups.find(
        (stop) => stopKeyOf(stop.id) === rootId
      );

      handleStopRemove(representative?.id ?? id);
    },
    [selectedStopGroups, handleStopRemove]
  );

  const handleStopSelect = React.useCallback(
    (stop: Stop) => {
      markSeen("stop", stopKeyOf(stop.id));
      onStopSelect(stop);
    },
    [markSeen, onStopSelect]
  );

  const handleSetActiveStop = React.useCallback(
    (id: string) => {
      markSeen("stop", stopKeyOf(id));
      setActiveStopId(id);
    },
    [markSeen, setActiveStopId]
  );

  const handleLineRemove = React.useCallback(
    (id: string) => {
      removeFromRecents("line", String(id));
      onLineRemove(id);
    },
    [removeFromRecents, onLineRemove]
  );

  const handleAddAndActivateLine = React.useCallback(
    (line: LineDetails | string) => {
      const id = typeof line === "string" ? String(line) : String(line.id);
      markSeen("line", id);
      onAddAndActivateLine(line);
    },
    [markSeen, onAddAndActivateLine]
  );

  const handleToggleShowLive = React.useCallback(
    (lineId: string, checked: boolean) => {
      markSeen("line", String(lineId));
      onToggleShowLiveForLine(lineId, checked);
    },
    [markSeen, onToggleShowLiveForLine]
  );

  const handleAddStopFromLine = React.useCallback(
    (s: Stop) => {
      markSeen("stop", stopKeyOf(s.id));
      onAddStopFromLine(s);
    },
    [markSeen, onAddStopFromLine]
  );

  const handleAddLineById = React.useCallback(
    (lineId: string) => {
      markSeen("line", String(lineId));
      onAddLineById(lineId);
    },
    [markSeen, onAddLineById]
  );

  const hasSelectedStopsOrLines =
    selectedStopGroups.length > 0 || selectedLines.length > 0;

  const handleClearStops = React.useCallback(() => {
    for (const stop of selectedStopGroups) {
      removeFromRecents("stop", stopKeyOf(stop.id));
    }

    onClearStops();
  }, [selectedStopGroups, removeFromRecents, onClearStops]);

  return (
    <div className="flex flex-col p-4 pt-0">
      <section
        className={`sticky top-0 z-10 mb-0 flex! flex-col gap-2 pt-4 bg-[var(--box-color-bg)] ${
          activeKind ? "pb-6" : "pb-6"
        }`}
      >
        <div className="!flex items-center justify-between gap-2">
          <Heading as="h3" className="mb-0">
            Aktiv
          </Heading>

          {activeKind && (
            <CompactButton
              variant="ghost"
              iconName="trash"
              size="small"
              showIcon
              className="rounded-sm!"
              onClick={onClearActive}
            >
              Rensa aktiv
            </CompactButton>
          )}
        </div>

        {activeKind === "stop" && activeStop && (
          <StopCard
            key={`active-stop-${activeStopKey}`}
            stop={activeStop}
            active
            onRemove={handleStopRemove}
            onSelect={handleStopSelect}
            setActive={handleSetActiveStop}
            onAddLineById={handleAddLineById}
            activeStopId={activeStopId}
            activeStopLocation={activeStopLocation}
            activeStopLocations={activeStopLocations}
            showStopLocationsButton={showStopLocationsButton}
            onShowStopLocations={onShowStopLocations}
            onHideStopLocations={onHideStopLocations}
          />
        )}

        {activeKind === "line" && activeLine && (
          <LineCard
            line={activeLine}
            active
            onRemove={handleLineRemove}
            onActivate={handleAddAndActivateLine}
            showLiveChecked={showLiveByLine.has(getLineId(activeLine))}
            onToggleShowLive={handleToggleShowLive}
            onAddStopFromLine={handleAddStopFromLine}
            allStops={allStops}
            selectedStopsGlobal={selectedStops}
            onRemoveStopGlobal={handleRemoveStopGlobalByParentId}
            liveUnavailable={Boolean(
              liveStateByLine?.[getLineId(activeLine)]?.unavailable
            )}
          />
        )}

        {!activeStop && visibleNartrafikAreaCount > 0 && (
          <Alert variant="info" showIcon role="status">
            <AlertTitle>Närtrafikområden visas fortfarande</AlertTitle>
            <AlertDescription className="flex flex-col items-start gap-3">
              <p className="mb-0">
                {visibleNartrafikAreaCount === 1
                  ? "1 närtrafikområde visas fortfarande på kartan."
                  : `${visibleNartrafikAreaCount} närtrafikområden visas fortfarande på kartan.`}
              </p>
              <CompactButton
                type="button"
                variant="ghost"
                size="small"
                onClick={onShowNartrafikAreas}
              >
                Visa områden
              </CompactButton>
            </AlertDescription>
          </Alert>
        )}

        {!activeKind && (
          <p className="mb-0 rounded-[var(--box-border-radius)] border border-[var(--card-color-border)] bg-[var(--card-color-bg)] p-4 flex items-center flex-row gap-2 ">
            <Icon
              name="info"
              className="bg-[var(--box-color-bg)] radius-[var(--badge-border-radius-default)] p-2 h-8 w-8"
            />
            <span>Ingen hållplats eller linje är vald.</span>
          </p>
        )}
      </section>

      <section>
        <div
          className={`mb-2 flex items-center justify-between gap-2 ${
            activeKind ? "pt-0" : "pt-0"
          }`}
        >
          <Heading as="h3" className="mb-0">
            Hållplatser
          </Heading>

          {selectedStopGroups.length > 0 && (
            <CompactButton
              variant="ghost"
              iconName="trash"
              size="small"
              showIcon
              onClick={handleClearStops}
              className="rounded-sm!"
            >
              Rensa hållplatser
            </CompactButton>
          )}
        </div>

        {selectedStopGroups.length > 0 ? (
          <ul className="mb-0 list-none flex flex-col gap-2 p-0">
            {selectedStopGroups.map((stop) => {
              const rootId = stopKeyOf(stop.id);

              const active = activeKind === "stop" && rootId === activeStopKey;

              return (
                <StopListButton
                  key={`stop-list-${rootId}`}
                  stop={stop}
                  active={active}
                  onActivate={handleStopSelect}
                  onRemove={handleStopRemove}
                />
              );
            })}
          </ul>
        ) : (
          <p className="mb-0 rounded-[var(--box-border-radius)] border border-[var(--card-color-border)] bg-[var(--card-color-bg)] p-4 items-center flex flex-row gap-2">
            <Icon
              name="sign"
              className="bg-[var(--box-color-bg)] radius-[var(--badge-border-radius-default)] p-2 h-8 w-8"
            />
            <span>Inga hållplatser valda.</span>
          </p>
        )}
      </section>

      <section>
        <div className="mb-2 pt-6 flex items-center justify-between gap-2">
          <Heading as="h3" className="mb-0">
            Linjer
          </Heading>

          {selectedLines.length > 0 && (
            <CompactButton
              variant="ghost"
              iconName="trash"
              size="small"
              showIcon
              className="rounded-sm!"
              onClick={() => {
                for (const line of selectedLines) {
                  handleLineRemove(getLineId(line));
                }
              }}
            >
              Rensa linjer
            </CompactButton>
          )}
        </div>
        {selectedLines.length > 0 ? (
          <ul className="mb-0 list-none flex flex-col gap-2 p-0">
            {sortedSelectedLines.map((line) => (
              <LineListButton
                key={`line-list-${getLineId(line)}`}
                line={line}
                active={
                  activeKind === "line" && lineMatchesActive(line, activeLineId)
                }
                onActivate={handleAddAndActivateLine}
                onRemove={handleLineRemove}
              />
            ))}
          </ul>
        ) : (
          <p className="mb-0 rounded-[var(--box-border-radius)] border border-[var(--card-color-border)] bg-[var(--card-color-bg)] p-4 flex flex-row items-center gap-2">
            <Icon
              name="route"
              className="bg-[var(--box-color-bg)] radius-[var(--badge-border-radius-default)] p-2 h-8 w-8"
            />{" "}
            <span>Inga linjer valda.</span>
          </p>
        )}
      </section>
    </div>
  );
}
